import { useContext, createContext, useEffect, useState } from 'react';
import throttle from 'lodash/throttle';

import { noop } from '@/utils';

const defaultState = {
  username: '',
  fullname: '',
};

type User = typeof defaultState;

export const AuthContext = createContext<User>(defaultState);

function doAuthRedirect() {
  window.location.replace(
    `${__API_URL__}/saml/login?redirect=${encodeURIComponent(window.location.href)}`
  );
}

export function _getSession() {
  // local phobos doesn't need auth because slauth sidecar isn't present
  // only relevant for frontend anyways, no security issues here
  if (__SHOULD_SKIP_AUTH__) {
    return Promise.resolve({
      username: 'test',
      fullname: 'test',
    });
  }

  return fetch(`${__API_URL__}/auth/check`, { credentials: 'include' })
    .then(res => {
      if (!res.ok) {
        doAuthRedirect();
        return Promise.reject(new Error('Auth fetch failed'));
      }
      return res.json();
    })
    .catch(() => {
      doAuthRedirect();
      // Reject the promise to surface the auth fetch error.
      // This is to solve: https://sentry.io/organizations/atlassian-2y/issues/3244034279/
      return Promise.reject(new Error('Auth fetch failed'));
    });
}

export const getSession = throttle(_getSession, 300, { trailing: false });

export function useAuthService() {
  const [user, setUser] = useState(defaultState);

  useEffect(() => {
    let isSubscribed = true;

    _getSession()
      .then((u: User) => {
        if (isSubscribed && u) {
          setUser(u);
        }
      })
      .catch(noop);

    return () => {
      isSubscribed = false;
    };
  }, []);

  return user;
}

function useAuth() {
  return useContext(AuthContext);
}

export default useAuth;
